import { useContext } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useIntl } from "react-intl";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { downloadPDF } from "@/features/common/helper";
import { AiBarChart } from "../charts-and-tables/AiBarChart";
import { AiLineChart } from "../charts-and-tables/AiLineChart";
import { AiPieChart } from "../charts-and-tables/AiPieChart";
import { AiTable } from "../charts-and-tables/AiTable";
import { exportConversationToPdf } from "../export-conversation/exportConversationService";
import {
  downloadChatTranscript,
  extractFirstMatchingToolCallPayload,
  extractTextFromMessageContent,
  parseBarChartFromToolArgs,
  parseLineChartFromToolArgs,
  parsePieChartFromToolArgs,
  parseSuggestedQuestionsFromToolArgs,
  parseTableFromToolArgs,
} from "../ai-chat-message-utils";
import type { AgentMessage, TableSortState } from "../ai-chat-message-utils";
import type { SourceProductConfig } from "../ai-chat-source-products";
import { useMessageCopyState } from "./useMessageCopyState";
import { useMessageExportState } from "./useMessageExportState";
import { useMessageSourcesState } from "./useMessageSourcesState";
import { AssistantMessageActions } from "./AssistantMessageActions";
import { MessageSources } from "./MessageSources";

interface AssistantMessageProps {
  message: Extract<AgentMessage, { role: "assistant" }>;
  sourceProducts?: SourceProductConfig[];
  tableSort?: TableSortState;
  onTableSortChange: (sort: TableSortState) => void;
}

export const AssistantMessage = ({
  message,
  sourceProducts = [],
  tableSort,
  onTableSortChange,
}: AssistantMessageProps) => {
  const intl = useIntl();
  const { institution } = useContext<AppContextInterface>(AppContext);
  const messageText = extractTextFromMessageContent(message.content);
  const { isCopied, copyResponseText } = useMessageCopyState(messageText);
  const { isExportingPdf, exportError, runPdfExport } = useMessageExportState();
  const toolCalls = message.toolCalls;
  const { isExpanded: isSourcesExpanded, toggleExpanded: toggleSourcesExpanded } =
    useMessageSourcesState();

  const assistantRoleLabel = intl.formatMessage({
    id: "app.toolboxChat.exportConversation.roleAssistant",
  });

  // Same card markup (ecp__card / ecp__role-label / ecp__markdown) and the
  // same "Currents" role label as the "Export conversation" panel's message
  // cards, so a single-message export matches that design exactly. These
  // classes are already defined in the backend's ai_chat_export.css.
  const exportAsPdf = () => {
    runPdfExport(async () => {
      const markdownHtml = renderToStaticMarkup(
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{messageText}</ReactMarkdown>,
      );
      const cardHtml =
        `<div class="tcw-message tcw-message--assistant">` +
        `<div class="ecp__card ecp__card--assistant">` +
        `<div class="ecp__message-column">` +
        `<p class="ecp__role-label">${assistantRoleLabel}</p>` +
        `<div class="tcw-markdown ecp__markdown ecp__markdown--expanded">${markdownHtml}</div>` +
        `</div></div></div>`;

      const header = intl.formatMessage({
        id: "app.toolboxChat.exportConversation.pdfHeaderTitle",
      });
      const result = await exportConversationToPdf(
        cardHtml,
        header,
        institution?.name ?? "",
      );
      downloadPDF(result.pdf_file_bytes, "Currents_Conversation");
    }, intl.formatMessage({ id: "app.toolboxChat.action.exportMessageError" }));
  };

  const exportAsText = () => {
    downloadChatTranscript(
      [message],
      { user: "", assistant: assistantRoleLabel },
      "Currents_Conversation",
    );
  };

  const barChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseBarChartFromToolArgs,
  );
  const pieChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parsePieChartFromToolArgs,
  );
  const lineChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseLineChartFromToolArgs,
  );
  const tablePayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseTableFromToolArgs,
  );
  const suggestedQuestionsPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseSuggestedQuestionsFromToolArgs,
  );

  const hasChartPayload =
    barChartPayload || pieChartPayload || lineChartPayload || tablePayload;

  if (hasChartPayload) {
    return (
      <div className="tcw-message tcw-message--assistant">
        <div className="tcw-assistant-wrapper">
          {barChartPayload && <AiBarChart payload={barChartPayload} />}
          {pieChartPayload && <AiPieChart payload={pieChartPayload} />}
          {lineChartPayload && <AiLineChart payload={lineChartPayload} />}
          {tablePayload && (
            <AiTable
              payload={tablePayload}
              sort={tableSort}
              onSortChange={onTableSortChange}
            />
          )}
          {messageText && (
            <div className="tcw-bubble tcw-bubble--assistant">
              <div className="tcw-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {messageText}
                </ReactMarkdown>
              </div>
            </div>
          )}
          {sourceProducts.length > 0 && (
            <MessageSources
              products={sourceProducts}
              isExpanded={isSourcesExpanded}
              onToggle={toggleSourcesExpanded}
              isAttachedToChart
            />
          )}
        </div>
      </div>
    );
  }

  if (suggestedQuestionsPayload && !messageText) {
    return null;
  }

  const pendingToolCalls =
    !messageText && toolCalls?.length ? toolCalls : null;

  return (
    <>
      <div className="tcw-message tcw-message--assistant">
        <div className="tcw-assistant-wrapper">
          <div className="tcw-bubble tcw-bubble--assistant">
            {messageText && (
              <div className="tcw-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {messageText}
                </ReactMarkdown>
              </div>
            )}
            {pendingToolCalls && (
              <p className="tcw-tool-call">
                {pendingToolCalls[0].function.name
                  .replace(/([A-Z])/g, " $1")
                  .toLowerCase()
                  .trim()}
                …
              </p>
            )}
          </div>
          {messageText && sourceProducts.length > 0 && (
            <MessageSources
              products={sourceProducts}
              isExpanded={isSourcesExpanded}
              onToggle={toggleSourcesExpanded}
            />
          )}
          {messageText && (
            <AssistantMessageActions
              isCopied={isCopied}
              onCopyResponseText={copyResponseText}
              onExportPdf={exportAsPdf}
              onExportText={exportAsText}
              isExportingPdf={isExportingPdf}
              exportError={exportError}
            />
          )}
        </div>
      </div>
    </>
  );
};
